import { Color, Group, Matrix, Path, Point, Project } from "paper";
import { PaperUtils } from "../utilities/paper-utils";
import { defaultEntityPathSettings, defaultTerrainPathSettings, defaultTriangleEntitySettings, defaultTextSettings, defaultGridLineSettings, defaultGridTextSettings, defaultSectionMarkerEntitySettings, pixelAttenuationOffset, defaultFlatGroundOutlineSettings, defaultMeasurementPanelSettings, MEASUREMENT_PANEL_HEIGHT_RATIO, MEASUREMENT_PANEL_SLOPE_LINE_VERTICAL_PX, MEASUREMENT_PANEL_SLOPE_LINE_DIAGONAL_WIDTH_PX, defaultMarkerLabelSettings, MARKER_LABEL_GAP_PX, } from "../settings/altitude-view-settings";
import { SlopeTextRender } from "../settings/viewer-settings";
import { AssetType, } from "../types";
import { Site } from "../site/site";
import { getMarkerLabelById } from "../site/alignment/section-marker-resolver";
import { computeVerticalLabelPlacement, computeVerticalLineStartY, selectVisibleMeasurementTexts, } from "./altitude-view-label-layout";
const CLICK_VS_DRAG_THRESHOLD_PX = 2;
/** Marker label rotation: 90° CCW on screen is +90° in this Y-flipped view. */
const MARKER_LABEL_ROTATION_DEG = 90;
/** Whether the pointer moved past the click threshold since the press; a missing press counts as a drag. */
function hasMovedPastClickThreshold(pressPosition, event) {
    if (!pressPosition)
        return true;
    return (Math.hypot(event.pageX - pressPosition.x, event.pageY - pressPosition.y) >=
        CLICK_VS_DRAG_THRESHOLD_PX);
}
/** Converts an item's project bounds to a normalized screen-pixel rectangle. */
function itemBoundsToScreenRect(item, view) {
    const a = view.projectToView(item.bounds.topLeft);
    const b = view.projectToView(item.bounds.bottomRight);
    return {
        left: Math.min(a.x, b.x),
        top: Math.min(a.y, b.y),
        right: Math.max(a.x, b.x),
        bottom: Math.max(a.y, b.y),
    };
}
function isSectionMarkerGlyphData(data) {
    if (typeof data !== "object" || data === null)
        return false;
    const candidate = data;
    return (candidate.name === "sectionMarker" &&
        typeof candidate.distanceFromStart === "number" &&
        typeof candidate.markerId === "string" &&
        typeof candidate.markerKind === "string");
}
/** Reads the associatedControlPointId set on a labeled control-point triangle's `Item.data`, if any. */
function getAssociatedControlPointId(data) {
    if (typeof data !== "object" || data === null)
        return undefined;
    const value = data.associatedControlPointId;
    return typeof value === "string" ? value : undefined;
}
// this info is to be shown on tooltip
export class AltitudeView {
    _project;
    _view;
    _canvas;
    // to keep all grid lines together and easy to delete all grid lines at same time.
    _gridGroup;
    _gridSizeInPixel = 50;
    _gridText;
    _entityRenderGroup;
    _controlPointRenderGroup;
    _cpNameTextRenderGroup;
    _textRenderGroup;
    _percentSlopeTextRenderGroup;
    _ratioSlopeTextRenderGroup;
    _degreeSlopeTextRenderGroup;
    _lengthTextRenderGroup;
    _heightTextRenderGroup;
    _totalDistanceRenderGroup;
    _slopeRenderType;
    _sectionMarkerRenderGroup;
    _sectionMarkerLabelRenderGroup;
    _sectionMarkerLabelData;
    _selectedTriangleIndex = undefined;
    _isSelectedTriangleSelectable = false;
    _pickScreenPosition;
    /** Press position for the section-marker click; kept separately since mouseup (before click) clears `_pickScreenPosition`. */
    _clickStartScreenPosition;
    _pickedTriangle;
    _pathMap;
    _currentEntityId;
    _entityDragEditCallback;
    _sectionMarkerSelectCallback;
    _onDoubleClickOnSlopeTextCallback;
    _tooltipCallback;
    _zoomFactor = 1.05;
    isDragging = false;
    startPoint;
    previewEntity;
    _pickEntityHandler;
    _moveEntityHandler;
    _placeEntityHandler;
    _selectEntityHandler;
    _sectionMarkerClickEventHandler;
    _entityPoints;
    _presentation;
    _previewSphere;
    _currentEntityType;
    _hideTooltipTimeout;
    _measurementPanelBackGroup; // panel background and top border, below the dashed lines
    _measurementPanelFrontGroup; // panel N-shape lines, triangles and texts, above the dashed lines
    _measurementPanelBackground;
    _verticalLineRenderGroup; // CP vertical dashed lines only, between panel back and front groups
    _measurementPanelData;
    _cpNameLabelData;
    get currentEntityType() {
        return this._currentEntityType;
    }
    get currentEntityID() {
        return this._currentEntityId;
    }
    constructor(presentation) {
        this._presentation = presentation;
        this._canvas = document.createElement("canvas");
        this._canvas.style.width = "100%";
        this._canvas.style.height = "100%";
        this._canvas.style.backgroundColor = "#566573";
        this._project = new Project(this._canvas);
        this._project.activate();
        this._view = this._project.view;
        this._view.transform(new Matrix(1, 0, 0, -1, 0, 0));
        // Rendering Sequence
        this._gridGroup = new Group();
        this._gridText = new Group();
        this._entityRenderGroup = new Group();
        this._textRenderGroup = new Group();
        this._percentSlopeTextRenderGroup = new Group();
        this._textRenderGroup.addChild(this._percentSlopeTextRenderGroup);
        this._ratioSlopeTextRenderGroup = new Group();
        this._textRenderGroup.addChild(this._ratioSlopeTextRenderGroup);
        this._degreeSlopeTextRenderGroup = new Group();
        this._textRenderGroup.addChild(this._degreeSlopeTextRenderGroup);
        this._heightTextRenderGroup = new Group();
        this._textRenderGroup.addChild(this._heightTextRenderGroup);
        this._lengthTextRenderGroup = new Group();
        this._textRenderGroup.addChild(this._lengthTextRenderGroup);
        this._totalDistanceRenderGroup = new Group();
        this._textRenderGroup.addChild(this._totalDistanceRenderGroup);
        this._controlPointRenderGroup = new Group();
        this._cpNameTextRenderGroup = new Group();
        this._sectionMarkerLabelRenderGroup = new Group();
        this._controlPointRenderGroup.onMouseEnter = () => {
            // console.log("Mouse Enter on control point", props);
        };
        this._sectionMarkerRenderGroup = new Group();
        this._pathMap = new Map();
        this._entityPoints = new Map();
        this._previewSphere = new Path.Circle(new Point(0, 0), 1);
        this._previewSphere.fillColor = new Color(1, 0, 0, 0.5);
        this._previewSphere.visible = false;
        //Create grid lines
        this.changeGridSize(this._gridSizeInPixel);
        this._canvas.addEventListener("wheel", (event) => {
            PaperUtils.zoom(event, this._zoomFactor, this._view);
            // changeGridSize runs _attenuate internally
            this.changeGridSize(this._gridSizeInPixel);
            this._activateView();
        });
        this._canvas.addEventListener("mousedown", (event) => {
            this.isDragging = true;
            this.startPoint = PaperUtils.getCoordinateOnView(this._view, event.pageX, event.pageY);
        });
        this._canvas.addEventListener("mousemove", (event) => {
            if (!this._selectedTriangleIndex && this.isDragging) {
                const point = PaperUtils.getCoordinateOnView(this._view, event.pageX, event.pageY);
                this._view.translate(point.subtract(this.startPoint));
                this.changeGridSize(this._gridSizeInPixel);
                this._view.requestUpdate();
            }
        });
        this._canvas.addEventListener("mouseup", () => {
            this.isDragging = false;
        });
        this._pickEntityHandler = this.pickEntity.bind(this);
        this._placeEntityHandler = this.placeEntity.bind(this);
        this._moveEntityHandler = this.move.bind(this);
        this._selectEntityHandler = this.selectEntity.bind(this);
        this._sectionMarkerClickEventHandler =
            this._sectionMarkerClickHandler.bind(this);
    }
    _activateView() {
        this._project.activate();
    }
    clearView() {
        // remove section markers (station points / BC-EC)
        this._sectionMarkerRenderGroup?.removeChildren();
        this._sectionMarkerLabelRenderGroup?.removeChildren();
        this._sectionMarkerLabelData = undefined;
        // remove control point entities
        this._controlPointRenderGroup.removeChildren();
        this._cpNameTextRenderGroup.removeChildren();
        //remove path
        this._entityRenderGroup.removeChildren();
        //remove texts
        this._percentSlopeTextRenderGroup.removeChildren();
        this._ratioSlopeTextRenderGroup.removeChildren();
        this._degreeSlopeTextRenderGroup.removeChildren();
        this._heightTextRenderGroup.removeChildren();
        this._lengthTextRenderGroup.removeChildren();
        this._totalDistanceRenderGroup.removeChildren();
        //remove grid text
        this._gridText.removeChildren();
        if (this._measurementPanelBackGroup) {
            this._measurementPanelBackGroup.remove();
            this._measurementPanelBackGroup = undefined;
        }
        if (this._verticalLineRenderGroup) {
            this._verticalLineRenderGroup.remove();
            this._verticalLineRenderGroup = undefined;
        }
        if (this._measurementPanelFrontGroup) {
            this._measurementPanelFrontGroup.remove();
            this._measurementPanelFrontGroup = undefined;
        }
        this._measurementPanelData = undefined;
        this._measurementPanelBackground = undefined;
        this._cpNameLabelData = undefined;
        this._currentEntityId = undefined;
    }
    resizeView(size) {
        if (size.height && size.width) {
            this._project.view.viewSize.height = size.height;
            this._project.view.viewSize.width = size.width;
            this._view.requestUpdate();
            this.changeGridSize(this._gridSizeInPixel);
        }
    }
    pickEntity(event) {
        if (event.button !== 0) {
            return;
        }
        this._activateView();
        this.startPoint = PaperUtils.getCoordinateOnView(this._view, event.pageX, event.pageY);
        this._pickScreenPosition = { x: event.pageX, y: event.pageY };
        this._clickStartScreenPosition = { x: event.pageX, y: event.pageY };
        const result = this.currentEntityType === AssetType.Road_Data
            ? this._hitTestOutsidePanel(this._controlPointRenderGroup, this.startPoint, { fill: true, stroke: true })
            : null;
        const isSelectableControlPoint = !!result?.item?.data?.isSelectableControlPoint;
        const isEnabled = result?.item?.data
            ? (result.item.data.isEnabled ?? true)
            : false;
        // Selectable triangles stay pickable when disabled; height-edit guard is in placeEntity().
        if (result?.item?.data && (isEnabled || isSelectableControlPoint)) {
            const controlPointId = result.item.data.associatedControlPointId;
            const percentage = result.item.data.percentage;
            this._selectedTriangleIndex = result.item.data.name;
            this._isSelectedTriangleSelectable = isSelectableControlPoint;
            this._pickedTriangle = {
                wasEnabled: isEnabled,
                originalX: result.item.position.x,
                // Disabled triangles skip the preview; move() clones it lazily once dragging starts.
                sourceItem: isEnabled ? result.item : undefined,
                selection: controlPointId
                    ? { kind: "controlPoint", controlPointId }
                    : percentage !== undefined
                        ? { kind: "slopeBreak", ratio: percentage }
                        : undefined,
            };
            this.isDragging = true;
        }
        else {
            // To prevent side effect of floating preview entity(slope break triangle) caused by
            // selectEntityHandler triggered on opening context menu
            this._selectedTriangleIndex = undefined;
            this._isSelectedTriangleSelectable = false;
            this._pickedTriangle = undefined;
        }
    }
    move(event) {
        if (event.button !== 0) {
            return;
        }
        const point = PaperUtils.getCoordinateOnView(this._view, event.pageX, event.pageY);
        //To check if cursor is intersecting with entity line, and if yes then show preview sphere.
        // Hover over the measurement panel never hits the entity line.
        const result = this._hitTestOutsidePanel(this._entityRenderGroup, point, {
            fill: true,
            stroke: true,
            segments: true,
        });
        if (result &&
            result.item.name === "entityPath" &&
            this.currentEntityType === AssetType.Road_Data) {
            this._previewSphere.position = point;
            this._previewSphere.visible = true;
        }
        else {
            this._previewSphere.visible = false;
        }
        if (this.isDragging && this._pickedTriangle?.sourceItem) {
            if (!this.previewEntity) {
                // Only clone the preview once it's an actual drag, so a plain click never shows it.
                if (hasMovedPastClickThreshold(this._pickScreenPosition, event)) {
                    this.previewEntity = this._pickedTriangle.sourceItem.clone();
                    this.previewEntity.selected = true;
                }
            }
            if (this.previewEntity) {
                this.previewEntity.position.y = point.y;
            }
        }
    }
    selectEntity(event) {
        this._activateView();
        this.startPoint = PaperUtils.getCoordinateOnView(this._view, event.pageX, event.pageY);
        const result = this._currentEntityType === AssetType.Road_Data
            ? this._hitTestOutsidePanel(this._controlPointRenderGroup, this.startPoint, {
                fill: true,
                stroke: true,
            })
            : null;
        if (result) {
            this._selectedTriangleIndex = result.item.data.name;
        }
    }
    async placeEntity(event) {
        if (event.button !== 0) {
            return;
        }
        this._activateView();
        const pickScreenPosition = this._pickScreenPosition;
        this._pickScreenPosition = undefined;
        const isClick = !hasMovedPastClickThreshold(pickScreenPosition, event);
        // Not previewEntity.position.x: disabled labeled triangles have no previewEntity.
        const previewPositionX = this._pickedTriangle?.originalX;
        if (this.previewEntity) {
            this.previewEntity.selected = false;
            this.previewEntity.remove();
            this.previewEntity = undefined;
        }
        if (this._pickedTriangle) {
            this._pickedTriangle.sourceItem = undefined;
        }
        this.isDragging = false;
        if (this._selectedTriangleIndex) {
            const selectedIndex = Number.parseInt(this._selectedTriangleIndex);
            if (Number.isFinite(selectedIndex)) {
                if (isClick && this._isSelectedTriangleSelectable) {
                    // Select the section instead of confirming a height edit.
                    if (this._sectionMarkerSelectCallback &&
                        this._currentEntityId &&
                        previewPositionX !== undefined) {
                        const data = this._pathMap.get(this._currentEntityId);
                        const offsetX = data?.pathPoints[0]?.[0];
                        const selection = this._pickedTriangle?.selection;
                        if (offsetX !== undefined && selection) {
                            const projectedDistance = previewPositionX - offsetX;
                            this._sectionMarkerSelectCallback(this._currentEntityId, selection.kind === "controlPoint"
                                ? {
                                    type: "controlPoint",
                                    controlPointId: selection.controlPointId,
                                }
                                : {
                                    type: "slopeBreak",
                                    ratio: selection.ratio,
                                    projectedDistance,
                                });
                        }
                    }
                }
                else if (this._pickedTriangle?.wasEnabled) {
                    // Regular drag: confirm the height edit. Disabled labeled
                    // triangles never reach here (wasEnabled is false for them),
                    // so dragging a disabled candidate point cannot set a height.
                    const viewPosition = PaperUtils.getCoordinateOnView(this._view, event.pageX, event.pageY);
                    //preparing data to send cesium
                    const height = viewPosition.y;
                    // const length = this._currentViewerUnit === ViewerUnitType.FEET ? EarthBrainUtils.convertFeetToMeter(viewPosition.x - this.startPoint.x) : viewPosition.x - this.startPoint.x;
                    // const data: ICesiumPointUpdateData = { index: selectedIndex, height: height, length: 0 };
                    const data = this._pathMap.get(this._currentEntityId);
                    data.slopeBreaks[selectedIndex].height = height;
                    this._entityDragEditCallback &&
                        (await this._entityDragEditCallback(this._currentEntityId, data.slopeBreaks.map((slopeBreak) => ({ ...slopeBreak }))));
                }
            }
            this._selectedTriangleIndex = undefined;
            this._isSelectedTriangleSelectable = false;
            this._pickedTriangle = undefined;
        }
    }
    enableEntityEdit(status, entityID, _entityDragEditCallback, _sectionMarkerSelectCallback, type, _onDoubleClickOnSlopeText) {
        this._activateView();
        this.isDragging = false;
        this._currentEntityType = type;
        const selectedPath = this._pathMap.get(entityID);
        if (selectedPath &&
            status &&
            entityID &&
            _entityDragEditCallback &&
            _sectionMarkerSelectCallback) {
            this._entityDragEditCallback = _entityDragEditCallback;
            this._sectionMarkerSelectCallback = _sectionMarkerSelectCallback;
            this._onDoubleClickOnSlopeTextCallback = _onDoubleClickOnSlopeText;
            this._currentEntityId = entityID;
            this._canvas.addEventListener("mousedown", this._pickEntityHandler);
            this._canvas.addEventListener("mouseup", this._placeEntityHandler);
            this._canvas.addEventListener("contextmenu", this._selectEntityHandler);
            this._canvas.addEventListener("mousemove", this._moveEntityHandler);
            this._canvas.addEventListener("click", this._sectionMarkerClickEventHandler);
        }
        else {
            this._entityDragEditCallback = undefined;
            this._sectionMarkerSelectCallback = undefined;
            this._onDoubleClickOnSlopeTextCallback = undefined;
            this._canvas.removeEventListener("mousedown", this._pickEntityHandler);
            this._canvas.removeEventListener("mouseup", this._placeEntityHandler);
            this._canvas.removeEventListener("contextmenu", this._selectEntityHandler);
            this._canvas.removeEventListener("mousemove", this._moveEntityHandler);
            this._canvas.removeEventListener("click", this._sectionMarkerClickEventHandler);
            this._previewSphere.visible = false;
        }
    }
    _createOutlinePoints(data) {
        const createPointsArr = (arr) => {
            return arr.map((a) => new Point(a[0], a[1]));
        };
        if (data.slopeBreaks.length === 0 || data.pathPoints.length === 0) {
            return {
                pathPoints: [],
                pointCloudPoints: [],
                designDataPoints: [],
                slopeBreakPoints: [],
                flatGroundDataPoints: [],
                referenceTopologyPoints: [],
            };
        }
        const slopePoints = this._convertSlopeBreaksToCoordinates(data.slopeBreaks, data.pathPoints);
        const cartDistSlopePoints = this._getCartDistanceBetnSlopeBreaks(data.slopeBreaks, data.pathPoints);
        // const pathPoints = createPointsArr(data.pathPoints);
        const pathPoints = createPointsArr(slopePoints).map((p, i) => {
            return { point: p, isEnabled: data.slopeBreaks[i].isActive ?? true };
        });
        const pointCloudPoints = data.pointCloudOutlines.map((d) => createPointsArr(d));
        const designDataPoints = data.designDataOutlines.map((d) => createPointsArr(d));
        const flatGroundDataPoints = data.flatGroundDataOutlines?.map((d) => createPointsArr(d)) || [];
        const slopeBreakPoints = createPointsArr(cartDistSlopePoints);
        const referenceTopologyPoints = data.referenceTopologyOutlines?.map((d) => createPointsArr(d)) || [];
        return {
            pathPoints,
            pointCloudPoints,
            designDataPoints,
            slopeBreakPoints: slopeBreakPoints,
            flatGroundDataPoints,
            referenceTopologyPoints,
        };
    }
    // This input data contains entity points and terrain points array
    //This function renders entity, height line, and terrain line
    renderData(entityId, data, fit, presentation, entityType) {
        //activate view and clear canvas
        this._activateView();
        this.clearView();
        this._presentation = presentation;
        if (entityId === undefined || data === undefined)
            return;
        //get bounding rect and resize the view
        const rect = this._canvas.getBoundingClientRect();
        this.resizeView({ width: rect.width, height: rect.height });
        //add data in path map
        this._pathMap.set(entityId, data);
        this._currentEntityId = entityId;
        this._currentEntityType = entityType;
        const offsetData = {
            ...data,
            pathPoints: data.pathPoints.map((pathPoint) => {
                return [pathPoint[0], pathPoint[1], pathPoint[2]];
            }),
            slopeBreaks: data.slopeBreaks.map((slopeBreak) => {
                return {
                    ...slopeBreak,
                    height: slopeBreak.height,
                };
            }),
        };
        this._entityPoints.set(entityId, this._createOutlinePoints(offsetData));
        //create entities
        this._createEntities(entityId, offsetData);
        if (fit) {
            //fit the entities in current view
            const bounds = this._controlPointRenderGroup.bounds;
            PaperUtils.fitView(this._view, bounds.center, bounds.width, bounds.height);
        }
        // changeGridSize runs _attenuate internally
        this.changeGridSize(this._gridSizeInPixel);
        this._activateView();
    }
    setBackgroundColor(color) {
        this._activateView();
        const tempColor = color ? color : "#566573";
        this._canvas.style.backgroundColor = tempColor;
    }
    changeGridSize(step) {
        this._activateView();
        PaperUtils.createGrid(this._view, step, this._gridGroup, this._gridText, this._presentation);
        this._attenuate();
    }
    getCanvas() {
        return this._canvas;
    }
    deleteEntityPath(id) {
        if (this._pathMap.delete(id) && this._currentEntityId === id) {
            this._currentEntityId = undefined;
            this.clearView();
        }
    }
    // TODO(D3D-739): unreachable — no callers.
    _addSlopeInbetweenControlPoints(roadID, slopeTextRenderType) {
        const roadData = this._entityPoints.get(roadID);
        if (!roadData)
            return;
        const pointsForRoadPath = roadData.pathPoints;
        pointsForRoadPath.forEach(({ point: currPoint }, i) => {
            const nextPoint = pointsForRoadPath[i + 1]?.point;
            if (nextPoint !== undefined) {
                //Find percentage slope
                const elevationBetCntrPoint = nextPoint.y - currPoint.y;
                const horizantalDistanceBetCntrPoint = Math.abs(currPoint.x - nextPoint.x);
                const percentSlope = (elevationBetCntrPoint / horizantalDistanceBetCntrPoint) * 100;
                const percentSlopeString = percentSlope.toFixed(3) + "%";
                const dir = nextPoint.subtract(currPoint).normalize();
                const percentSlopeFirstText = PaperUtils.createText(currPoint, defaultTextSettings.fillColor, percentSlopeString, 1);
                percentSlopeFirstText.bringToFront();
                percentSlopeFirstText.data.moveDir = dir.clone();
                percentSlopeFirstText.data.refPosition =
                    percentSlopeFirstText.position.clone();
                percentSlopeFirstText.data.slopeIndex = i;
                percentSlopeFirstText.data.slopeValue = percentSlope;
                percentSlopeFirstText.data.slopeUnit = "percent";
                const percentSlopeSecondText = PaperUtils.createText(nextPoint, defaultTextSettings.fillColor, percentSlopeString, 1);
                percentSlopeSecondText.bringToFront();
                percentSlopeSecondText.data.moveDir = dir.clone().multiply(-1);
                percentSlopeSecondText.data.refPosition =
                    percentSlopeSecondText.position.clone();
                percentSlopeSecondText.data.slopeIndex = i;
                percentSlopeSecondText.data.slopeValue = percentSlope;
                percentSlopeSecondText.data.slopeUnit = "percent";
                percentSlopeFirstText.onDoubleClick =
                    this.handleDoubleClickOnText.bind(this);
                percentSlopeSecondText.onDoubleClick =
                    this.handleDoubleClickOnText.bind(this);
                this._percentSlopeTextRenderGroup.addChild(percentSlopeFirstText);
                this._percentSlopeTextRenderGroup.addChild(percentSlopeSecondText);
                //calculate the ratio slope
                const slopeNumerator = nextPoint.y - currPoint.y;
                const slopeDenominator = nextPoint.x - currPoint.x;
                const ratioSlopeString = slopeNumerator.toFixed(3) + "/" + slopeDenominator.toFixed(3);
                const ratioSlopeText = PaperUtils.createText(currPoint, defaultTextSettings.fillColor, ratioSlopeString, 1);
                ratioSlopeText.bringToFront();
                ratioSlopeText.data.moveDir = dir.clone();
                ratioSlopeText.data.refPosition = ratioSlopeText.position.clone();
                this._ratioSlopeTextRenderGroup.addChild(ratioSlopeText);
                const degreeSlopString = ((Math.atan(slopeNumerator / slopeDenominator) * 180) /
                    Math.PI).toFixed(3) + "°";
                const degreeSlopeText = PaperUtils.createText(currPoint, defaultTextSettings.fillColor, degreeSlopString, 1);
                degreeSlopeText.bringToFront();
                degreeSlopeText.data.moveDir = dir.clone();
                degreeSlopeText.data.refPosition = degreeSlopeText.position.clone();
                this._degreeSlopeTextRenderGroup.addChild(degreeSlopeText);
            }
        });
        this.updateSlopeRenderingType(slopeTextRenderType);
    }
    /**
     * This function hides the texts that are colliding with each other.
     * First the slope texts are hidden, Later the length texts are hidden
     * Height text and total length text is not hidden
     */
    _checkCollisionAmongTexts() {
        const totalTextBounds = this._totalDistanceRenderGroup.children.map((i) => i.bounds);
        const heightTextBounds = this._heightTextRenderGroup.children.map((i) => i.bounds);
        const lengthTextBounds = this._lengthTextRenderGroup.children.map((i) => i.bounds);
        const slopeTextGroup = this._getTextBasedOnSlopeType(this._slopeRenderType);
        if (slopeTextGroup) {
            const slopeTextBounds = slopeTextGroup.children.map((i) => i.bounds);
            // Checking slope text
            let slopeTextIntersection = false;
            for (let i = 0; i < slopeTextBounds.length; ++i) {
                const box = slopeTextBounds[i];
                const restSlopeBoxes = [...slopeTextBounds];
                restSlopeBoxes.splice(i, 1);
                slopeTextIntersection = [
                    ...restSlopeBoxes,
                    ...lengthTextBounds,
                    ...heightTextBounds,
                    ...totalTextBounds,
                ].some((i) => i.intersects(box));
                if (slopeTextIntersection)
                    break;
            }
            slopeTextGroup.visible = !slopeTextIntersection;
        }
        // Check length text
        let lengthTextIntersection = false;
        for (let i = 0; i < lengthTextBounds.length; ++i) {
            const box = lengthTextBounds[i];
            lengthTextIntersection = [...heightTextBounds, ...totalTextBounds].some((i) => i.intersects(box));
            if (lengthTextIntersection)
                break;
        }
        this._lengthTextRenderGroup.visible = !lengthTextIntersection;
    }
    _attenuate() {
        //attenuate Entities
        this._controlPointRenderGroup &&
            this._controlPointRenderGroup.children.forEach((triangle) => {
                PaperUtils.attenuateEntites(triangle, this._view, defaultTriangleEntitySettings.defaultPixelSize);
            });
        //attenuate section markers (station points / BC-EC)
        this._sectionMarkerRenderGroup &&
            this._sectionMarkerRenderGroup.children.forEach((item) => {
                PaperUtils.attenuateEntites(item, this._view, defaultSectionMarkerEntitySettings.defaultPixelSize);
            });
        //attenuate lines
        this._entityRenderGroup &&
            this._entityRenderGroup.children.forEach((path) => {
                PaperUtils.attenuateLines(path, this._view, defaultEntityPathSettings.defaultPixelSize);
            });
        //attenuate texts
        this._percentSlopeTextRenderGroup.children.forEach((text) => {
            PaperUtils.attenuateEntites(text, this._view, defaultTextSettings.defaultPixelSize);
            PaperUtils.setAttenuatedPos(text, pixelAttenuationOffset.slopeTextOffsetAltitudeView, this._view);
        });
        this._ratioSlopeTextRenderGroup.children.forEach((text) => {
            PaperUtils.attenuateEntites(text, this._view, defaultTextSettings.defaultPixelSize);
            PaperUtils.setAttenuatedPos(text, pixelAttenuationOffset.slopeTextOffsetAltitudeView, this._view);
        });
        this._degreeSlopeTextRenderGroup.children.forEach((text) => {
            PaperUtils.attenuateEntites(text, this._view, defaultTextSettings.defaultPixelSize);
            PaperUtils.setAttenuatedPos(text, pixelAttenuationOffset.slopeTextOffsetAltitudeView, this._view);
        });
        this._heightTextRenderGroup.children.forEach((text) => {
            PaperUtils.attenuateEntites(text, this._view, defaultTextSettings.defaultPixelSize);
            PaperUtils.setAttenuatedPos(text, pixelAttenuationOffset.heightTextOffsetAltitudeView, this._view);
        });
        this._lengthTextRenderGroup.children.forEach((text) => {
            PaperUtils.attenuateEntites(text, this._view, defaultTextSettings.defaultPixelSize);
            PaperUtils.setAttenuatedPos(text, pixelAttenuationOffset.lentghTextOffsetAltitudeView, this._view);
        });
        this._totalDistanceRenderGroup.children.forEach((text) => {
            PaperUtils.attenuateEntites(text, this._view, defaultTextSettings.defaultPixelSize);
            PaperUtils.setAttenuatedPos(text, pixelAttenuationOffset.totalTextOffsetAltitudeView, this._view);
        });
        const cpNameLabelBottoms = this._drawMarkerLabels();
        //attenuate Grids
        this._gridGroup &&
            this._gridGroup.children.forEach((gridLine) => {
                PaperUtils.attenuateLines(gridLine, this._view, defaultGridLineSettings.defaultPixelSize);
            });
        this._gridText &&
            this._gridText.children.forEach((text) => {
                PaperUtils.attenuateEntites(text, this._view, defaultGridTextSettings.defaultPixelSize);
            });
        this._previewSphere &&
            PaperUtils.attenuateEntites(this._previewSphere, this._view, defaultGridTextSettings.defaultPixelSize);
        // Re-draw measurement panel
        if (this._measurementPanelBackGroup &&
            this._measurementPanelFrontGroup &&
            this._measurementPanelData) {
            this._measurementPanelBackGroup.removeChildren();
            this._measurementPanelFrontGroup.removeChildren();
            this._drawMeasurementPanelContents();
        }
        // Reads the redrawn panel background, so must follow the panel redraw
        this._drawVerticalLines(cpNameLabelBottoms);
        // Check Collision
        this._checkCollisionAmongTexts();
        this._view.requestUpdate();
    }
    _getTextBasedOnSlopeType(type) {
        switch (type) {
            case SlopeTextRender.RATIO:
                return this._ratioSlopeTextRenderGroup;
            case SlopeTextRender.PERCENT:
                return this._percentSlopeTextRenderGroup;
            case SlopeTextRender.DEGREE:
                return this._degreeSlopeTextRenderGroup;
            default:
                return undefined;
        }
    }
    updateSlopeRenderingType(slopeTextRenderType) {
        //Hide both slope text render group before updating visibility
        this._percentSlopeTextRenderGroup.visible = false;
        this._ratioSlopeTextRenderGroup.visible = false;
        this._degreeSlopeTextRenderGroup.visible = false;
        this._slopeRenderType = slopeTextRenderType;
        //update the visibility based on slopeRenderType
        const group = this._getTextBasedOnSlopeType(slopeTextRenderType);
        group && (group.visible = true);
    }
    updatePresentationAndReRender(presentation) {
        this._activateView();
        this.clearView();
        this._presentation = presentation;
        const data = this._pathMap.get(this._currentEntityId);
        if (!data) {
            return;
        }
        this._entityPoints.set(this._currentEntityId, this._createOutlinePoints(data));
        //rerender the entity
        this._createEntities(this._currentEntityId, data);
        //fit the entities in current view
        const bounds = this._controlPointRenderGroup.bounds;
        PaperUtils.fitView(this._view, bounds.center, bounds.width, bounds.height);
        //attenuate them
        this._attenuate();
    }
    _renderCtrlPointAndHeightText(pointsForRoadPath, slopeBreaks, selectedX) {
        const cpLabelIndices = slopeBreaks
            .map((sb, i) => ({
            index: i,
            hasCP: !!sb.associatedControlPointId,
        }))
            .filter((item) => item.hasCP)
            .map((item) => item.index);
        const cpNameLabels = [];
        const site = Site.getInstance();
        const alignment = site?.getAlignment(this._currentEntityId);
        pointsForRoadPath.forEach((pointForRoadPath, index) => {
            const triangle = PaperUtils.createRegularPolygon(pointForRoadPath.point.clone(), defaultTriangleEntitySettings.sides, defaultTriangleEntitySettings.radius, pointForRoadPath.isEnabled
                ? defaultTriangleEntitySettings.fillColor
                : defaultTriangleEntitySettings.disabledColor);
            if (!site) {
                return;
            }
            const road = site.getRoad(this._currentEntityId);
            const isRoadData = !!road;
            if (road) {
                const slopeBreakInfo = road.getSlopeBreakInfo(index);
                triangle.data = slopeBreakInfo;
                triangle.data.isRoad = true;
                triangle.data.name = index.toString();
                triangle.onMouseEnter = (t) => {
                    clearTimeout(this._hideTooltipTimeout);
                    if (!this.isDragging) {
                        // (this as any).fillColor = 'red'; // Change color on hover
                        this._showTriangleToolTip(true, t.target);
                        triangle.onMouseLeave = () => {
                            // (this as any).fillColor = 'blue'; // Revert to original color
                            this._hideTooltipTimeout = window.setTimeout(() => {
                                this._showTriangleToolTip(false);
                            }, 200);
                        };
                    }
                    else {
                        triangle.onMouseLeave = undefined;
                    }
                };
            }
            else {
                triangle.data = {
                    name: index.toString(),
                    associatedControlPointId: slopeBreaks[index]?.associatedControlPointId,
                };
            }
            triangle.position.set(pointForRoadPath.point.clone());
            // Define the onMouseEnter event handler
            this._controlPointRenderGroup.addChild(triangle);
            // Add CP name text if this index is in cpLabelIndices
            const cpLabelPosition = cpLabelIndices.indexOf(index);
            const isLabeledControlPoint = cpLabelPosition !== -1;
            triangle.data.isLabeledControlPoint = isLabeledControlPoint;
            const isSelectableControlPoint = isRoadData || isLabeledControlPoint;
            triangle.data.isSelectableControlPoint = isSelectableControlPoint;
            if (isSelectableControlPoint) {
                const isSelected = Math.abs(pointForRoadPath.point.x - selectedX) < 1e-3;
                if (isSelected) {
                    triangle.fillColor = defaultSectionMarkerEntitySettings.selectedColor;
                }
            }
            if (isLabeledControlPoint) {
                let cpName;
                if (cpLabelPosition === 0) {
                    cpName = "BP";
                }
                else if (cpLabelPosition === cpLabelIndices.length - 1) {
                    cpName = "EP";
                }
                else {
                    const ipId = slopeBreaks[index]?.associatedControlPointId;
                    cpName =
                        (ipId && alignment && getMarkerLabelById(alignment, ipId)) ??
                            `IP${cpLabelPosition}`;
                }
                cpNameLabels.push({
                    text: cpName,
                    anchor: pointForRoadPath.point.clone(),
                    pointIndex: index,
                });
            }
        });
        this._cpNameLabelData = cpNameLabels;
    }
    setTooltipCallback(callback) {
        this._tooltipCallback = callback;
    }
    _showTriangleToolTip(show, triangle) {
        if (show && triangle) {
            if (triangle.data) {
                const triangleInfo = triangle.data;
                const dataToRender = [
                    {
                        title: "Height",
                        data: triangleInfo.height.toFixed(3),
                    },
                    {
                        title: "Length On Datum",
                        data: triangleInfo.length2d.toFixed(3),
                    },
                    {
                        title: "Length",
                        data: triangleInfo.length3d.toFixed(3),
                    },
                    {
                        title: "Point",
                        data: [
                            {
                                title: "X",
                                data: triangleInfo.pointOnPath.x.toFixed(3),
                            },
                            {
                                title: "Y",
                                data: triangleInfo.pointOnPath.y.toFixed(3),
                            },
                            {
                                title: "Z",
                                data: triangleInfo.pointOnPath.z.toFixed(3),
                            },
                        ],
                    },
                    {
                        title: "Control Point",
                        data: triangleInfo.controlPoint
                            ? [
                                {
                                    title: "name",
                                    data: triangleInfo.controlPoint.name,
                                },
                                {
                                    title: "Point",
                                    data: [
                                        {
                                            title: "X",
                                            data: triangleInfo.controlPoint.point.x.toFixed(3),
                                        },
                                        {
                                            title: "Y",
                                            data: triangleInfo.controlPoint.point.y.toFixed(3),
                                        },
                                        {
                                            title: "Z",
                                            data: triangleInfo.controlPoint.point.z.toFixed(3),
                                        },
                                    ],
                                },
                            ]
                            : "Not Available",
                    },
                ];
                const viewPos = this._view.projectToView(triangle.position);
                if (this._tooltipCallback) {
                    this._tooltipCallback(dataToRender, { x: viewPos.x, y: viewPos.y }, true);
                }
            }
        }
        else {
            if (this._tooltipCallback) {
                // this._tooltipCallback(null, { x: 0, y: 0 }, false);
            }
        }
    }
    _renderDistanceBetnSlopeBreaks(slopeBreakPoints, pathPoints) {
        const unitLabel = this._presentation.getUnitLabel();
        slopeBreakPoints.forEach((currentPoint, i) => {
            const nextPoint = slopeBreakPoints[i + 1];
            if (nextPoint !== undefined) {
                const dist = nextPoint.x - currentPoint.x;
                const pos = pathPoints[i].add(pathPoints[i + 1]).multiply(0.5);
                const lengthText = PaperUtils.createText(pos, defaultTextSettings.fillColor, `${this._presentation.formatAxisValue(dist, 3)}${unitLabel}`, 1);
                lengthText.data.moveDir = new Point(0, 1);
                lengthText.data.refPosition = lengthText.position.clone();
                this._lengthTextRenderGroup.addChild(lengthText);
            }
        });
    }
    _renderTotalDistanceAtSlopeBreak(slopeBreakPoints, pathPoints) {
        const unitLabel = this._presentation.getUnitLabel();
        slopeBreakPoints.forEach((currentPoint, i) => {
            const nextPoint = slopeBreakPoints[i + 1];
            if (nextPoint !== undefined) {
                const dist = nextPoint.x;
                const textString = ` [ ${this._presentation.formatAxisValue(dist, 3)}${unitLabel} ]`;
                const lengthText = PaperUtils.createText(pathPoints[i + 1], defaultTextSettings.fillColor, textString, 1);
                lengthText.data.moveDir = new Point(0, 1);
                lengthText.data.refPosition = lengthText.position.clone();
                this._totalDistanceRenderGroup.addChild(lengthText);
            }
        });
    }
    _renderSectionMarkers(data) {
        if (!this._sectionMarkerRenderGroup)
            return;
        this._sectionMarkerRenderGroup.removeChildren();
        const pathPoints = data.pathPoints;
        if (!data.sectionMarkers?.length || pathPoints.length === 0) {
            this._sectionMarkerLabelData = [];
            return;
        }
        const offsetX = pathPoints[0][0];
        const selectedX = data.sectionLineDistFromFirstPoint;
        const labels = [];
        data.sectionMarkers.forEach((marker) => {
            const x = offsetX + marker.distanceFromStart;
            const height = this._findHeightAtDistance(pathPoints, x);
            if (height === undefined)
                return;
            const point = new Point(x, height);
            const isSelected = Math.abs(x - selectedX) < 1e-3;
            const color = marker.kind === "station"
                ? defaultSectionMarkerEntitySettings.stationColor
                : defaultSectionMarkerEntitySettings.curvePointColor;
            const glyph = marker.kind === "station"
                ? PaperUtils.createPoint(point.clone(), defaultSectionMarkerEntitySettings.stationRadius, color)
                : PaperUtils.createPoint(point.clone(), defaultSectionMarkerEntitySettings.curvePointRadius, color);
            if (isSelected) {
                glyph.fillColor = defaultSectionMarkerEntitySettings.selectedColor;
            }
            glyph.data.name = "sectionMarker";
            glyph.data.distanceFromStart = marker.distanceFromStart;
            glyph.data.markerId = marker.id;
            glyph.data.markerKind = marker.kind;
            this._sectionMarkerRenderGroup.addChild(glyph);
            labels.push({
                text: marker.label,
                anchor: point.clone(),
            });
        });
        this._sectionMarkerLabelData = labels;
    }
    /** Linear-interpolates the height (y) at the given distance (x) along pathPoints. */
    _findHeightAtDistance(pathPoints, distance) {
        if (pathPoints.length === 0)
            return undefined;
        if (distance <= pathPoints[0][0])
            return pathPoints[0][1];
        for (let i = 1; i < pathPoints.length; i++) {
            if (pathPoints[i][0] >= distance) {
                const prev = pathPoints[i - 1];
                const curr = pathPoints[i];
                const span = curr[0] - prev[0];
                if (span === 0)
                    return prev[1];
                const ratio = (distance - prev[0]) / span;
                return prev[1] + (curr[1] - prev[1]) * ratio;
            }
        }
        return pathPoints[pathPoints.length - 1][1];
    }
    /** Whether a project-space point is inside the drawn measurement panel background. */
    _isInMeasurementPanel(point) {
        if (this._measurementPanelBackground === undefined)
            return false;
        return this._measurementPanelBackground.bounds.contains(point);
    }
    /** Hit-tests the group, treating points inside the measurement panel as no hit. */
    _hitTestOutsidePanel(group, point, options) {
        if (!group)
            return null;
        if (this._isInMeasurementPanel(point))
            return null;
        return group.hitTest(point.clone(), options);
    }
    /** Handles a single-shot click on a section marker glyph, independent from the drag flow. */
    _sectionMarkerClickHandler(event) {
        const clickStartScreenPosition = this._clickStartScreenPosition;
        this._clickStartScreenPosition = undefined;
        if (event.button !== 0 || !this._sectionMarkerRenderGroup)
            return;
        if (!this._sectionMarkerSelectCallback || !this._currentEntityId)
            return;
        // DOM click fires after any press/release on the canvas, including pans and drags.
        if (hasMovedPastClickThreshold(clickStartScreenPosition, event))
            return;
        const point = PaperUtils.getCoordinateOnView(this._view, event.pageX, event.pageY);
        const result = this._hitTestOutsidePanel(this._sectionMarkerRenderGroup, point, {
            fill: true,
            stroke: true,
        });
        if (result?.item && isSectionMarkerGlyphData(result.item.data)) {
            const { distanceFromStart, markerKind, markerId } = result.item.data;
            this._sectionMarkerSelectCallback(this._currentEntityId, {
                type: "sectionMarker",
                markerId,
                markerKind,
                projectedDistance: distanceFromStart,
            });
            return;
        }
        // Trench: BP/EP/IP triangle markers are click-selectable too (drag remains Road-only).
        if (this._currentEntityType === AssetType.Trench_Data) {
            const controlPointResult = this._hitTestOutsidePanel(this._controlPointRenderGroup, point, { fill: true, stroke: true });
            if (controlPointResult?.item) {
                const controlPointId = getAssociatedControlPointId(controlPointResult.item.data);
                if (controlPointId) {
                    this._sectionMarkerSelectCallback(this._currentEntityId, {
                        type: "controlPoint",
                        controlPointId,
                    });
                }
            }
        }
    }
    _createPaperPathForPoints(pointList, strokeColor) {
        const path = new Path();
        path.strokeColor = strokeColor;
        pointList.forEach((point) => path.add(point.clone()));
        return path;
    }
    _createPaperSlopePathForPoints(pointList, strokeColor) {
        const path = new Path();
        path.strokeColor = strokeColor;
        pointList.forEach((point) => path.add(point.clone()));
        return path;
    }
    _convertPercentageToDistance(percentage, startDistance, endDistance) {
        return percentage * (endDistance - startDistance);
    }
    _createEntities(entityId, data) {
        const entityInfo = this._entityPoints.get(entityId);
        if (!entityInfo)
            return;
        const entityPointsInfo = entityInfo;
        //should be similar to entityPath
        // const slopePathPoints = ro
        //create entityPath and Terrain Path
        const pathPointsOnlyPoints = entityPointsInfo.pathPoints.map((p) => p.point);
        const entityPath = this._createPaperPathForPoints(pathPointsOnlyPoints, defaultEntityPathSettings.strokeColor);
        entityPath.name = "entityPath";
        entityPath.bringToFront();
        const terrainPaths = entityPointsInfo.pointCloudPoints.map((d) => this._createPaperPathForPoints(d, defaultTerrainPathSettings.strokeColor));
        const designColor = new Color(1, 0, 1);
        const designOutLinePaths = entityPointsInfo.designDataPoints.map((d) => this._createPaperPathForPoints(d, designColor));
        const refereneTopologyColor = new Color(165 / 256, 42 / 256, 42 / 256);
        const referenceTopologyPaths = entityPointsInfo.referenceTopologyPoints.map((d) => this._createPaperPathForPoints(d, refereneTopologyColor));
        const flatGroundOutLinePaths = entityPointsInfo.flatGroundDataPoints.map((d) => this._createPaperPathForPoints(d, defaultFlatGroundOutlineSettings.strokeColor));
        this._entityRenderGroup.addChildren(designOutLinePaths);
        this._entityRenderGroup.addChildren(referenceTopologyPaths);
        this._entityRenderGroup.addChildren(flatGroundOutLinePaths);
        this._entityRenderGroup.addChildren(terrainPaths);
        this._entityRenderGroup.addChild(entityPath);
        this._renderCtrlPointAndHeightText(entityPointsInfo.pathPoints, data.slopeBreaks, data.sectionLineDistFromFirstPoint);
        this._renderMeasurementPanel(entityId, data);
        this._renderSectionMarkers(data);
    }
    pickPathAndPoint(x, y) {
        const data = {
            index: undefined,
            height: undefined,
            length: undefined,
        };
        const result = {
            pathId: undefined,
            pointIndex: undefined,
            isEnabled: true,
            data,
        };
        if (this._currentEntityType === AssetType.Trench_Data)
            return result;
        const point = PaperUtils.getCoordinateOnView(this._view, x, y);
        let tempResult = this._hitTestOutsidePanel(this._controlPointRenderGroup, point, {
            fill: true,
            stroke: true,
        });
        if (!tempResult) {
            tempResult = this._hitTestOutsidePanel(this._entityRenderGroup, point, {
                fill: true,
                stroke: true,
                segments: true,
            });
        }
        if (tempResult) {
            if (tempResult.item.name === "entityPath") {
                result.pathId = this._currentEntityId;
                result.pointIndex = tempResult.item.name;
                // const data = this.getIndexToAdd(this._currentRoadId, point.x);
                const data = this._getInfoForEntityPath(this._currentEntityId, point);
                result.data.index = data.index;
                result.data.height = data.height;
                // Set the percent value
                result.data.length = data.percent;
            }
            else {
                const slopeBreakInfo = tempResult.item.data;
                result.pathId = this._currentEntityId;
                result.pointIndex = slopeBreakInfo.index.toString();
                result.isEnabled = slopeBreakInfo.isEnabled;
            }
        }
        return result;
    }
    _convertSlopeBreaksToCoordinates(slopeBreaks, pathPoints) {
        // get the x distance of the percentage
        // y will be coming from 3D only
        return slopeBreaks.map((s) => {
            if (s.percentage === 0) {
                return [pathPoints[0][0], s.height];
            }
            if (s.percentage === 1) {
                return [pathPoints[pathPoints.length - 1][0], s.height];
            }
            // find the 3D dist
            const dist2DForSLopePercent = this._convertPercentageToDistance(s.percentage, pathPoints[0][0], pathPoints[pathPoints.length - 1][0]);
            return [dist2DForSLopePercent, s.height];
        });
    }
    _getCartDistanceBetnSlopeBreaks(slopeBreaks, pathPoints) {
        if (pathPoints.length < 2 || slopeBreaks.length < 2)
            throw new Error("Invalid data");
        // get the cartesian distance of the percentage
        // y will be coming from 3D only
        return slopeBreaks.map((s) => {
            if (s.percentage === 0) {
                return [pathPoints[0][2], s.height];
            }
            if (s.percentage === 1) {
                return [pathPoints[pathPoints.length - 1][2], s.height];
            }
            // find the 3D dist
            const dist2DForSLopePercent = this._convertPercentageToDistance(s.percentage, pathPoints[0][0], pathPoints[pathPoints.length - 1][0]);
            let beforeIndex = -1;
            let afterIndex = -1;
            // find the before and after indexs of the points in which the slope % fall
            for (let i = 0; i < pathPoints.length; i++) {
                if (pathPoints[i][0] >= dist2DForSLopePercent) {
                    // find the 2D values
                    beforeIndex = i - 1;
                    afterIndex = i;
                    break;
                }
            }
            // Handle edge cases
            if (beforeIndex < 0) {
                beforeIndex = 0;
                afterIndex = 1;
            }
            const xDiff = pathPoints[afterIndex][0] - pathPoints[beforeIndex][0];
            if (xDiff === 0) {
                return [pathPoints[beforeIndex][2], s.height];
            }
            const xValue3D = pathPoints[beforeIndex][2] +
                ((pathPoints[afterIndex][2] - pathPoints[beforeIndex][2]) / xDiff) *
                    (dist2DForSLopePercent - pathPoints[beforeIndex][0]);
            // Return cartesian distance corresponding to slopeBreak.
            return [xValue3D, s.height];
        });
    }
    _getSlopeBreaks(roadId) {
        const data = this._pathMap.get(roadId);
        return data.slopeBreaks;
    }
    /**
     * Returns % value and index for a point that needs to be added in the 2D view
     * @param entityId road id
     * @param xDistIn2D X Distance of the point selected
     * @returns
     */
    _getInfoForEntityPath(entityId, selectedPoint) {
        const entityData = this._entityPoints.get(entityId);
        const pathPoints = entityData?.pathPoints ?? [];
        const nextIndex = pathPoints.findIndex((p) => p.point.x > selectedPoint.x);
        if (nextIndex === -1) {
            return {
                index: pathPoints.length - 1,
                percent: 1,
                height: pathPoints[pathPoints.length - 1].point.y,
            };
        }
        const prevIndex = nextIndex - 1;
        if (prevIndex === -1) {
            return { index: 0, percent: 0, height: pathPoints[0].point.y };
        }
        const slopeNumerator = pathPoints[nextIndex].point.y - pathPoints[prevIndex].point.y;
        const slopeDenominator = pathPoints[nextIndex].point.x - pathPoints[prevIndex].point.x;
        const percent = selectedPoint.x / pathPoints[pathPoints.length - 1].point.x;
        if (slopeDenominator === 0) {
            return {
                index: prevIndex,
                percent: percent,
                height: pathPoints[prevIndex].point.y,
            };
        }
        const slope = slopeNumerator / slopeDenominator;
        const height = slope * (selectedPoint.x - pathPoints[prevIndex].point.x) +
            pathPoints[prevIndex].point.y;
        return { index: prevIndex, percent, height };
    }
    removeSelectedSlopeBreak(roadId) {
        if (this._selectedTriangleIndex) {
            const selectedIndex = Number.parseInt(this._selectedTriangleIndex);
            const selectedPath = this._pathMap.get(roadId);
            const slopeBreaks = [...selectedPath.slopeBreaks];
            if (!isNaN(selectedIndex) &&
                selectedIndex > 0 &&
                selectedIndex < slopeBreaks.length - 1) {
                slopeBreaks.splice(selectedIndex, 1);
                this._selectedTriangleIndex = undefined;
                this._isSelectedTriangleSelectable = false;
                this._pickedTriangle = undefined;
                if (this.previewEntity) {
                    this.previewEntity.selected = false;
                    this.previewEntity.remove();
                    this.previewEntity = undefined;
                }
                return slopeBreaks;
            }
            else {
                console.warn("Slope break index is not valid", selectedIndex);
            }
        }
        this._selectedTriangleIndex = undefined;
        this._isSelectedTriangleSelectable = false;
        this._pickedTriangle = undefined;
    }
    _renderMeasurementPanel(entityId, data) {
        const entityInfo = this._entityPoints.get(entityId);
        if (!entityInfo)
            return;
        const entityPointsInfo = entityInfo;
        const cpPaperPoints = entityPointsInfo.pathPoints.map((p) => p.point);
        this._measurementPanelData = {
            cpPaperPoints,
            slopeBreaks: data.slopeBreaks,
        };
        // Layer order: panel back < vertical dashed lines < panel front
        this._measurementPanelBackGroup = new Group();
        this._measurementPanelBackGroup.insertAbove(this._sectionMarkerRenderGroup);
        this._verticalLineRenderGroup = new Group();
        this._verticalLineRenderGroup.insertAbove(this._measurementPanelBackGroup);
        this._measurementPanelFrontGroup = new Group();
        this._measurementPanelFrontGroup.insertAbove(this._verticalLineRenderGroup);
    }
    /**
     * Clears and redraws all marker labels (BP/IP/EP and NO.n / BC / EC) from the kept data.
     * @returns screen-px bottom of each drawn CP name label, keyed by pathPoints index
     */
    _drawMarkerLabels() {
        const cpNameGroup = this._cpNameTextRenderGroup;
        const sectionMarkerGroup = this._sectionMarkerLabelRenderGroup;
        if (!cpNameGroup || !sectionMarkerGroup)
            return new Map();
        cpNameGroup.removeChildren();
        sectionMarkerGroup.removeChildren();
        const cpNameLabels = this._cpNameLabelData ?? [];
        const cpNameBottoms = this._drawVerticalMarkerLabels(cpNameLabels, cpNameGroup, defaultMarkerLabelSettings.controlPoint);
        this._drawVerticalMarkerLabels(this._sectionMarkerLabelData ?? [], sectionMarkerGroup, defaultMarkerLabelSettings.sectionMarker);
        return new Map(cpNameLabels.map((label, i) => [label.pointIndex, cpNameBottoms[i]]));
    }
    /**
     * Draws marker labels rotated 90° CCW, top end just below each anchor, into the group.
     * @returns screen-px bottom of each drawn label, in input order
     */
    _drawVerticalMarkerLabels(labels, group, style) {
        const view = this._view;
        const bottoms = [];
        for (const label of labels) {
            // --- create text and measure its on-screen length
            const text = PaperUtils.createText(label.anchor.clone(), defaultTextSettings.fillColor, label.text, 1);
            // Scale before rotating: attenuateEntites normalizes bounds.height
            PaperUtils.attenuateEntites(text, view, style.fontPixelSize);
            const rect = itemBoundsToScreenRect(text, view);
            const lengthPx = rect.right - rect.left;
            // --- place below the anchor, rotate, record bottom
            const anchorPx = view.projectToView(label.anchor);
            const center = computeVerticalLabelPlacement({
                anchor: { x: anchorPx.x, y: anchorPx.y },
                lengthPx,
                clearancePx: style.clearancePx,
            });
            text.rotate(MARKER_LABEL_ROTATION_DEG);
            text.position = view.viewToProject(new Point(center.x, center.y));
            group.addChild(text);
            // Take bottom from drawn bounds (max screen y, Y-flipped view safe)
            bottoms.push(itemBoundsToScreenRect(text, view).bottom);
        }
        return bottoms;
    }
    /**
     * Clears and redraws each CP's vertical dashed line, from its computed start down to the screen bottom.
     * @param labelBottoms screen-px bottom of the CP name label, keyed by pathPoints index
     */
    _drawVerticalLines(labelBottoms) {
        // --- guard and clear
        const group = this._verticalLineRenderGroup;
        const background = this._measurementPanelBackground;
        const panelData = this._measurementPanelData;
        if (!group || !background || !panelData)
            return;
        group.removeChildren();
        // --- panel rect and dash style in current zoom
        const view = this._view;
        // Panel top and screen bottom come from the drawn background, in screen px
        const panelRect = itemBoundsToScreenRect(background, view);
        const lineWidth = PaperUtils.pixelToProjectDist(defaultMeasurementPanelSettings.verticalLinePixelSize, view);
        const dashProj = PaperUtils.pixelToProjectDist(defaultMeasurementPanelSettings.verticalLineDashPx[0], view);
        const gapProj = PaperUtils.pixelToProjectDist(defaultMeasurementPanelSettings.verticalLineDashPx[1], view);
        // --- one dashed line per CP
        panelData.cpPaperPoints.forEach((cpPoint, index) => {
            const cpPx = view.projectToView(cpPoint);
            const startY = computeVerticalLineStartY({
                markerBottomY: cpPx.y + defaultTriangleEntitySettings.defaultPixelSize / 2,
                labelBottomY: labelBottoms.get(index) ?? null,
                panelTopY: panelRect.top,
                gapPx: MARKER_LABEL_GAP_PX,
            });
            const start = view.viewToProject(new Point(cpPx.x, startY));
            const end = view.viewToProject(new Point(cpPx.x, panelRect.bottom));
            const vertLine = new Path();
            vertLine.strokeColor = defaultMeasurementPanelSettings.verticalLineColor;
            vertLine.dashArray = [dashProj, gapProj];
            vertLine.strokeWidth = lineWidth;
            vertLine.add(new Point(cpPoint.x, start.y));
            vertLine.add(new Point(cpPoint.x, end.y));
            group.addChild(vertLine);
        });
    }
    _drawMeasurementPanelContents() {
        const backGroup = this._measurementPanelBackGroup;
        const frontGroup = this._measurementPanelFrontGroup;
        if (!this._measurementPanelData || !backGroup || !frontGroup)
            return;
        const { cpPaperPoints, slopeBreaks } = this._measurementPanelData;
        const unitLabel = this._presentation.getUnitLabel();
        const view = this._view;
        const heightProj = view.bounds.height * MEASUREMENT_PANEL_HEIGHT_RATIO;
        // view.bounds.top = visual bottom in Y-flipped coordinate system
        const panelBottom = view.bounds.top;
        const panelTop = panelBottom + heightProj;
        const panelLeft = view.bounds.left;
        const panelWidth = view.bounds.width;
        const baselineY = panelBottom + heightProj * 0.3;
        // Background rectangle
        const bgRect = new Path();
        bgRect.add(new Point(panelLeft, panelBottom));
        bgRect.add(new Point(panelLeft + panelWidth, panelBottom));
        bgRect.add(new Point(panelLeft + panelWidth, panelTop));
        bgRect.add(new Point(panelLeft, panelTop));
        bgRect.closed = true;
        bgRect.fillColor = defaultMeasurementPanelSettings.backgroundColor;
        bgRect.strokeColor = null;
        backGroup.addChild(bgRect);
        this._measurementPanelBackground = bgRect;
        // Top border line
        const panelTopLineWidth = PaperUtils.pixelToProjectDist(defaultMeasurementPanelSettings.panelTopLinePixelSize, view);
        const topBorder = new Path();
        topBorder.strokeColor = defaultMeasurementPanelSettings.panelTopLineColor;
        topBorder.strokeWidth = panelTopLineWidth;
        topBorder.add(new Point(panelLeft, panelTop));
        topBorder.add(new Point(panelLeft + panelWidth, panelTop));
        backGroup.addChild(topBorder);
        const lineWidth = PaperUtils.pixelToProjectDist(defaultMeasurementPanelSettings.verticalLinePixelSize, view);
        const verticalProj = PaperUtils.pixelToProjectDist(MEASUREMENT_PANEL_SLOPE_LINE_VERTICAL_PX, view);
        const diagonalWidthProj = PaperUtils.pixelToProjectDist(MEASUREMENT_PANEL_SLOPE_LINE_DIAGONAL_WIDTH_PX, view);
        const panelTextEntries = [];
        // Per-segment: N-shape polyline, slope text, distance text
        for (let i = 0; i < cpPaperPoints.length - 1; i++) {
            const cpX1 = cpPaperPoints[i].x;
            const cpX2 = cpPaperPoints[i + 1].x;
            const centerX = (cpX1 + cpX2) / 2;
            const diagLeft = centerX - diagonalWidthProj / 2;
            const diagRight = centerX + diagonalWidthProj / 2;
            const dy = cpPaperPoints[i + 1].y - cpPaperPoints[i].y;
            const dx = Math.abs(cpX2 - cpX1);
            const percentSlope = dx !== 0 ? (dy / dx) * 100 : 0;
            // N-shape polyline (flat slope → simple horizontal line)
            const nPath = new Path();
            nPath.strokeColor = defaultMeasurementPanelSettings.verticalLineColor;
            nPath.strokeWidth = lineWidth;
            if (Math.abs(percentSlope) < 0.0005) {
                // Flat slope: simple horizontal line
                nPath.add(new Point(cpX1, baselineY));
                nPath.add(new Point(cpX2, baselineY));
            }
            else {
                const isAscending = dy > 0;
                nPath.add(new Point(cpX1, baselineY));
                nPath.add(new Point(diagLeft, baselineY));
                if (isAscending) {
                    nPath.add(new Point(diagLeft, baselineY - verticalProj));
                    nPath.add(new Point(diagRight, baselineY + verticalProj));
                }
                else {
                    nPath.add(new Point(diagLeft, baselineY + verticalProj));
                    nPath.add(new Point(diagRight, baselineY - verticalProj));
                }
                nPath.add(new Point(diagRight, baselineY));
                nPath.add(new Point(cpX2, baselineY));
            }
            frontGroup.addChild(nPath);
            // Distance/Slope text positions:
            // default: ratio-based from baseline
            // overflow: if i overflows panel top, switch both i/L to fixed positions from panelTop
            const distTextOffsetPx = 12;
            const distTextOffsetProj = PaperUtils.pixelToProjectDist(distTextOffsetPx, view);
            const distTextYRatio = baselineY + distTextOffsetProj;
            const slopeTextOffsetPx = 12;
            const slopeTextOffsetProj = PaperUtils.pixelToProjectDist(slopeTextOffsetPx, view);
            const slopeTextYRatio = distTextYRatio + slopeTextOffsetProj;
            const slopeTextMinMarginPx = 10;
            const slopeTextMinMarginProj = PaperUtils.pixelToProjectDist(slopeTextMinMarginPx, view);
            const slopeTextYMax = panelTop - slopeTextMinMarginProj;
            const isSlopeOverflow = slopeTextYRatio > slopeTextYMax;
            const fixedSlopeOffsetFromTopPx = 10;
            const fixedSlopeOffsetFromTopProj = PaperUtils.pixelToProjectDist(fixedSlopeOffsetFromTopPx, view);
            const fixedDistGapFromSlopePx = 12;
            const fixedDistGapFromSlopeProj = PaperUtils.pixelToProjectDist(fixedDistGapFromSlopePx, view);
            const slopeTextY = isSlopeOverflow
                ? panelTop - fixedSlopeOffsetFromTopProj
                : slopeTextYRatio;
            const distTextY = isSlopeOverflow
                ? slopeTextY - fixedDistGapFromSlopeProj
                : distTextYRatio;
            const distStr = `L=${this._presentation.formatFixed(dx, 3)}${unitLabel}`;
            const distTextItem = PaperUtils.createText(new Point(centerX, distTextY), defaultMeasurementPanelSettings.textFillColor, distStr, 1);
            PaperUtils.attenuateEntites(distTextItem, view, defaultMeasurementPanelSettings.textPixelSize);
            distTextItem.position = new Point(centerX, distTextY);
            frontGroup.addChild(distTextItem);
            panelTextEntries.push({
                item: distTextItem,
                key: { kind: "distance", segmentIndex: i },
                anchorProjX: centerX,
            });
            // Slope text i=N.NNN%
            const slopeStr = `i=${percentSlope.toFixed(3)}%`;
            const slopeTextItem = PaperUtils.createText(new Point(centerX, slopeTextY), defaultMeasurementPanelSettings.textFillColor, slopeStr, 1);
            PaperUtils.attenuateEntites(slopeTextItem, view, defaultMeasurementPanelSettings.textPixelSize);
            slopeTextItem.data.slopeIndex = i;
            slopeTextItem.data.slopeValue = percentSlope;
            slopeTextItem.data.slopeUnit = "percent";
            slopeTextItem.onDoubleClick = this.handleDoubleClickOnText.bind(this);
            slopeTextItem.position = new Point(centerX, slopeTextY);
            frontGroup.addChild(slopeTextItem);
            panelTextEntries.push({
                item: slopeTextItem,
                key: { kind: "slope", segmentIndex: i },
                anchorProjX: centerX,
            });
        }
        // Per-CP (pass 2): triangle marker and height text (drawn above N-shape)
        for (let i = 0; i < cpPaperPoints.length; i++) {
            const cpX = cpPaperPoints[i].x;
            const markerY = baselineY;
            // Triangle marker below baseline (Y-flipped: visual lower position)
            const isEnabled = slopeBreaks[i].isActive ?? true;
            const triangle = PaperUtils.createRegularPolygon(new Point(cpX, markerY), defaultTriangleEntitySettings.sides, defaultTriangleEntitySettings.radius, isEnabled
                ? defaultTriangleEntitySettings.fillColor
                : defaultTriangleEntitySettings.disabledColor);
            PaperUtils.attenuateEntites(triangle, view, defaultTriangleEntitySettings.defaultPixelSize);
            triangle.position = new Point(cpX, markerY);
            frontGroup.addChild(triangle);
            // Height text H=N.NNN
            const htTextOffsetPx = 20;
            const htTextOffsetProj = PaperUtils.pixelToProjectDist(htTextOffsetPx, view);
            const htTextYRatio = markerY + htTextOffsetProj;
            const minMarginFromTopPx = 10;
            const minMarginProj = PaperUtils.pixelToProjectDist(minMarginFromTopPx, view);
            const htTextYMax = panelTop - minMarginProj;
            const htTextY = Math.min(htTextYRatio, htTextYMax);
            const heightStr = `FH=${this._presentation.formatFixed(slopeBreaks[i].height, 3)}${unitLabel}`;
            const htItem = PaperUtils.createText(new Point(cpX, htTextY), defaultMeasurementPanelSettings.textFillColor, heightStr, 1);
            PaperUtils.attenuateEntites(htItem, view, defaultMeasurementPanelSettings.textPixelSize);
            htItem.position = new Point(cpX, htTextY);
            frontGroup.addChild(htItem);
            panelTextEntries.push({
                item: htItem,
                key: { kind: "height", pointIndex: i },
                anchorProjX: cpX,
            });
        }
        // Hide lower-priority texts overlapping on screen (uses final positions)
        const visibleFlags = selectVisibleMeasurementTexts(panelTextEntries.map((entry) => ({
            key: entry.key,
            anchorX: view.projectToView(new Point(entry.anchorProjX, baselineY)).x,
            rect: itemBoundsToScreenRect(entry.item, view),
        })));
        for (let index = 0; index < panelTextEntries.length; index++) {
            panelTextEntries[index].item.visible = visibleFlags[index];
        }
    }
    handleDoubleClickOnText(e) {
        const ev = e;
        if (this._onDoubleClickOnSlopeTextCallback) {
            this._onDoubleClickOnSlopeTextCallback(ev.event, ev.target.data);
        }
    }
}
//# sourceMappingURL=view-2d-altitude.js.map